import * as THREE from 'three';

/*
 * The rune seal on the Warden's door (crypt in embers): three stone plates on the floor of the last room
 * (four in the deep crypt's rune puzzle),
 * each carved with a rune of its own. Dormant they show dim carved grooves with a faint ember in them;
 * while the sim shows the order each one flashes bright in turn; plates stepped on in the right order stay
 * lit; solved, all three burn. One merged mesh, one draw; the state of each plate rides in its vertices.
 */

const VERT = /* glsl */ `
attribute vec2 aLocal;
attribute vec3 aPlate;
varying vec2 vL;
varying vec3 vP;
void main() {
  vL = aLocal;
  vP = aPlate;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}`;

/** vP: x the rune (0 … 3), y lit (0 → 1, steady), z flash (0 → 1, the order being shown). */
const FRAG = /* glsl */ `
uniform float uTime;
varying vec2 vL;
varying vec3 vP;
float seg(vec2 p, vec2 a, vec2 b) {
  vec2 pa = p - a, ba = b - a;
  float h = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0);
  return length(pa - ba * h);
}
float rune(vec2 p, float id) {
  float d = 9.0;
  if (id < 0.5) {
    // Algiz: a stave with two arms raised.
    d = min(d, seg(p, vec2(0.0, -0.42), vec2(0.0, 0.42)));
    d = min(d, seg(p, vec2(0.0, 0.02), vec2(-0.3, 0.38)));
    d = min(d, seg(p, vec2(0.0, 0.02), vec2(0.3, 0.38)));
  } else if (id < 1.5) {
    // Ing: a diamond with crossed tails.
    d = min(d, seg(p, vec2(0.0, -0.3), vec2(0.27, 0.0)));
    d = min(d, seg(p, vec2(0.27, 0.0), vec2(0.0, 0.3)));
    d = min(d, seg(p, vec2(0.0, 0.3), vec2(-0.27, 0.0)));
    d = min(d, seg(p, vec2(-0.27, 0.0), vec2(0.0, -0.3)));
    d = min(d, seg(p, vec2(0.0, 0.3), vec2(0.0, 0.44)));
    d = min(d, seg(p, vec2(0.0, -0.3), vec2(0.0, -0.44)));
  } else if (id < 2.5) {
    // Thurisaz: a stave with a thorn.
    d = min(d, seg(p, vec2(-0.16, -0.42), vec2(-0.16, 0.42)));
    d = min(d, seg(p, vec2(-0.16, 0.24), vec2(0.22, 0.0)));
    d = min(d, seg(p, vec2(0.22, 0.0), vec2(-0.16, -0.24)));
  } else {
    // Sowilo: a bolt (the deep crypt's fourth plate).
    d = min(d, seg(p, vec2(0.2, 0.42), vec2(-0.2, 0.12)));
    d = min(d, seg(p, vec2(-0.2, 0.12), vec2(0.2, -0.12)));
    d = min(d, seg(p, vec2(0.2, -0.12), vec2(-0.2, -0.42)));
  }
  return d;
}
void main() {
  // Hexagonal plate: distance to its edge in the hex metric.
  vec2 a = abs(vL);
  float hx = max(a.x * 0.866 + a.y * 0.5, a.y);
  float lit = vP.y;
  float fl = vP.z;
  float glow = max(lit * 0.7, fl);
  float r = rune(vL, vP.x);
  float groove = 1.0 - smoothstep(0.035, 0.06, r);
  float halo = 1.0 - smoothstep(0.04, 0.22, r);
  float ring = exp(-pow((length(vL) - 0.66) / 0.03, 2.0));
  float bevel = smoothstep(0.84, 0.9, hx);
  float inside = 1.0 - smoothstep(0.96, 1.0, hx);
  vec3 stoneC = mix(vec3(0.035, 0.031, 0.032), vec3(0.014, 0.011, 0.011), bevel);
  float breathe = 0.75 + 0.25 * sin(uTime * 1.6 + vP.x * 2.1);
  vec3 dim = vec3(0.32, 0.05, 0.02) * breathe;
  vec3 hot = mix(vec3(1.0, 0.2, 0.08), vec3(1.0, 0.4, 0.16), fl);
  stoneC += vec3(0.09, 0.012, 0.004) * lit * (1.0 - bevel) * (0.8 + 0.2 * breathe);
  vec3 col = mix(stoneC, vec3(0.02, 0.01, 0.01), groove);
  col += dim * groove * (1.0 - glow);
  col += hot * (groove * 1.5 + halo * 0.3) * glow;
  col += mix(vec3(0.18, 0.03, 0.015), hot, glow) * ring * (0.35 + 0.9 * glow);
  gl_FragColor = vec4(col, inside);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}`;

const SEGS = 6;
const RINGS = 4;

export interface SealLook {
  plates: readonly { q: number; r: number }[];
  order: readonly number[];
  step: number;
  showFrom: number;
  showUntil: number;
  solved: boolean;
}

/** The state of one plate at a tick: lit (steady, 0/1) and flash (0 → 1, while the order is shown). */
export const plateState = (s: SealLook, i: number, tick: number): { lit: number; flash: number } => {
  const n = Math.max(1, s.order.length);
  const show = Math.max(1, Math.round((s.showUntil - s.showFrom) / n));
  let flash = 0;
  if (tick >= s.showFrom && tick < s.showUntil) {
    const k = Math.floor((tick - s.showFrom) / show);
    // Bright as it lights, fading a little toward the next, with a dark beat between two.
    const t = (tick - s.showFrom - k * show) / show;
    if (s.order[k] === i) flash = t > 0.85 ? 0.25 : 1 - t * 0.35;
  }
  const lit = s.solved || s.order.slice(0, s.step).includes(i) ? 1 : 0;
  return { lit, flash };
};

export class SealPlates {
  readonly mesh: THREE.Mesh;
  private state: THREE.BufferAttribute;
  private u = { uTime: { value: 0 } };
  readonly at: THREE.Vector3[];

  constructor(centers: THREE.Vector3[], size: number) {
    this.at = centers;
    const pos: number[] = [];
    const local: number[] = [];
    const plate: number[] = [];
    const index: number[] = [];
    const per = 1 + RINGS * SEGS;
    centers.forEach((c, n) => {
      const base = n * per;
      pos.push(c.x, c.y, c.z);
      local.push(0, 0);
      plate.push(n % 4, 0, 0);
      for (let i = 1; i <= RINGS; i++) {
        const k = i / RINGS;
        for (let s = 0; s < SEGS; s++) {
          // Pointy side toward ±z, like the board's hexes; the corner sits at radius 1 / cos 30°.
          const a = (Math.PI / 3) * s + Math.PI / 6;
          const lx = (Math.cos(a) * k) / 0.866, lz = (Math.sin(a) * k) / 0.866;
          pos.push(c.x + lx * size, c.y, c.z + lz * size);
          local.push(lx, -lz);
          plate.push(n % 4, 0, 0);
        }
      }
      for (let s = 0; s < SEGS; s++) index.push(base, base + 1 + ((s + 1) % SEGS), base + 1 + s);
      for (let i = 1; i < RINGS; i++) {
        const a0 = base + 1 + (i - 1) * SEGS, b0 = base + 1 + i * SEGS;
        for (let s = 0; s < SEGS; s++) {
          const s1 = (s + 1) % SEGS;
          index.push(a0 + s, a0 + s1, b0 + s, a0 + s1, b0 + s1, b0 + s);
        }
      }
    });
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    geo.setAttribute('aLocal', new THREE.Float32BufferAttribute(local, 2));
    this.state = new THREE.Float32BufferAttribute(plate, 3).setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('aPlate', this.state);
    geo.setIndex(index);
    this.mesh = new THREE.Mesh(
      geo,
      new THREE.ShaderMaterial({
        uniforms: this.u,
        vertexShader: VERT,
        fragmentShader: FRAG,
        transparent: true,
        depthWrite: false,
        polygonOffset: true,
        polygonOffsetFactor: -4,
        polygonOffsetUnits: -8,
      }),
    );
    this.mesh.name = 'seal-plates';
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 1;
  }

  /** Writes each plate's state for this tick; returns them for the view's glow and sparks. */
  sync(s: SealLook, tick: number): { lit: number; flash: number }[] {
    const arr = this.state.array as Float32Array;
    const per = 1 + RINGS * SEGS;
    const out = this.at.map((_, i) => plateState(s, i, tick));
    out.forEach((st, i) => {
      for (let v = 0; v < per; v++) {
        arr[(i * per + v) * 3 + 1] = st.lit;
        arr[(i * per + v) * 3 + 2] = st.flash;
      }
    });
    this.state.needsUpdate = true;
    return out;
  }

  frame(now: number): void {
    this.u.uTime.value = now % 3600;
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
  }
}
